import { Fetcher } from '@this/src/util';
import React, { lazy, Suspense } from 'react';
import type { LinkProps, RouteComponentProps } from 'react-router-dom';
import { Route, Switch } from 'react-router-dom';

import Link from '@this/shared/atoms/link';
import { AITThemeContext, useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { AdminRoleResponse } from '@this/domain/admin_role/admin_role';
import AdminRole from '@this/domain/admin_role/admin_role';
import { BulkTicketsPage } from '@this/components/admin/bulk_tickets/bulk_tickets.page';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import RepositoryDisplayAdjustment from '@this/components/admin/repository_display_adjustment/repository_display_adjustment';
import Manuals from '@this/components/admin/manuals/manuals';
import NotFound from '@this/components/admin/not_found/not_found';
import Knowledge_images from '@this/components/admin/KnowledgeImages/knowledge_images';
import OrganizationAnnouncements from '@this/components/admin/organization_announcements/organization_announcements';
import IndividualTargetSuppliedItems from '@this/components/admin/supplied_item_mappings/individual_target_supplied_items';
import IndividualOptionSuppliedItems from '@this/components/admin/supplied_item_mappings/individual_option_supplied_items';
import IndividualRailwaySuppliedItems from '@this/src/components/admin/supplied_item_mappings/individual_railway_supplied_items/individual_railway_supplied_items';
import IndividualDomesticAirSuppliedItems from '@this/components/admin/supplied_item_mappings/individual_domestic_air_supplied_items';
import { reportError } from '@this/lib/bugsnag';
import ArrangementReports from './arrangement_reports/arrangement_reports';
import AdminRoles from './admin_roles';
import AdminHeader from './admin_header/admin_header';
import Footer from '../shared/footer/footer';

const Dashboard = lazy(() => import('./dashboard/dashboard'));
const Admins = lazy(() => import('./admins'));
const AdminUserEdit = lazy(() => import('./admin_user_edit/admin_user_edit'));
const Invoices = lazy(() => import('./invoices/invoices'));
const InvoicesEdit = lazy(() => import('./invoices/invoices_edit'));
const Accounting = lazy(() => import('./accounting/accounting'));
const Organizations = lazy(() => import('./organizations/organizations'));
const OrganizationDetail = lazy(() => import('./organization_detail/organization_detail'));
const OnlineApplications = lazy(() => import('./online_applications/online_application_list'));
const OnlineApplicationNew = lazy(() => import('./online_applications/online_application_new_form'));
const OnlineApplicationEdit = lazy(() => import('./online_applications/online_application_edit_form'));
const OnlineApplicationPreview = lazy(() => import('./online_applications/online_application_preview'));
const OnlineApplicationConfirm = lazy(() => import('./online_applications/online_application_confirm'));
const OnlineApplicationDetail = lazy(() => import('./online_applications/online_application_detail'));
const ArrangeSteps = lazy(() => import('./arrange_steps/arrange_steps'));
const ArrangeCategories = lazy(() => import('./arrange_categories/arrange_categories'));
const MasterStepSequences = lazy(() => import('./master_step_sequences/master_step_sequences'));
const MasterSteps = lazy(() => import('./master_steps/master_steps'));
const MasterStepMappings = lazy(() => import('./master_step_mappings/master_step_mappings'));
const MasterStepMappingsV2 = lazy(() => import('./master_step_mappings/master_step_mappings_v2'));
const MasterStepSettings = lazy(() => import('./master_step_settings/master_step_settings'));
const MasterStepSequenceHistories = lazy(
  () => import('./master_step_sequence_histories/master_step_sequence_histories')
);
const MasterStepSequenceHistory = lazy(
  () => import('./master_step_sequence_histories/master_step_sequence_history')
);
const Holidays = lazy(() => import('./holidays/holidays'));
const Suppliers = lazy(() => import('./suppliers/suppliers'));
const SupplierDetail = lazy(() => import('./supplier_detail/supplier_detail'));
const SuppliedItemMappings = lazy(() => import('./supplied_item_mappings/supplied_item_mappings'));
const SupplierHistories = lazy(() => import('./supplier_histories/supplier_histories'));
const SupplierHistory = lazy(() => import('./supplier_histories/supplier_history'));
const SuppliedItems = lazy(() => import('./supplied_items/supplied_items'));
const SupplierCards = lazy(() => import('./supplier_cards/supplier_cards'));
const AuditLogs = lazy(() => import('./audit_logs/audit_logs'));
const SupplyCategoryDetail = lazy(() => import('./supply_category_detail/supply_category_detail'));
const PaymentMethods = lazy(() => import('./payment_methods/payment_methods'));
const PaymentMethodHistories = lazy(() => import('./payment_method_histories/payment_method_histories'));
const PaymentMethodHistory = lazy(() => import('./payment_method_histories/payment_method_history'));
const SavingAccounts = lazy(() => import('./saving_accounts/saving_accounts'));
const OrganizationDashboards = lazy(() => import('./organization_dashboards/organization_dashboards'));
const OrganizationDashboardDetail = lazy(() => import('./organization_dashboards/organization_dashboard_detail'));
const Informations = lazy(() => import('./informations/informations'));
const ShareholderTicketFees = lazy(() => import('./shareholder_ticket_fees/shareholder_ticket_fees'));
const Arrangers = lazy(() => import('./arrangers/arrangers'));
const AdminAiChatTrips = lazy(() => import('./ai_chat_trips/AdminAiChatTrips'));
const AdminAiChatTripDetail = lazy(() => import('./ai_chat_trips/AdminAiChatTripDetail'));

type Props = RouteComponentProps;

interface State {
  roleLoading: boolean;
  readablePaths: string[];
  writablePaths: string[];
}

const navItems = [
  { path: '', label: 'ダッシュボード' },
  { path: 'organizations', label: '顧客マスタ' },
  { path: 'online_applications', label: '申込' },
  { path: 'admins', label: '管理者マスタ' },
  { path: 'admin_roles', label: '管理者役割設定' },
  { path: 'arrangers', label: '手配者マスタ' },
  { path: 'master_step_sequences', label: '手配ステップ設定' },
  { path: 'invoices', label: '請求書' },
  { path: 'accounting/trial_balance', label: '会計' },
  { path: 'arrangement_reports', label: '手配関連集計' },
  { path: 'bulk_tickets', label: '回数券' },
  { path: 'manuals', label: 'マニュアル' },
  { path: 'knowledge_images', label: 'ファイルアップロード' },
  { path: 'informations', label: 'お知らせ' },
  { path: 'organization_announcements', label: '管理者向けのお知らせ' },
  { path: 'shareholder_ticket_fees', label: '株主優待券' },
  { path: 'repository_display_adjustment', label: '在庫表示調整' },
  { path: 'holidays', label: '休日設定' },
  { path: 'suppliers', label: '仕入先マスタ' },
  { path: 'payment_methods', label: '決済方法マスタ' },
  { path: 'saving_accounts', label: '入金口座マスタ' },
  { path: 'supplied_item_mappings', label: '仕入商品マッピング' },
  { path: 'supplier_cards', label: '仕入カード登録' },
  { path: 'audit_logs', label: '特権アカウント作業ログ' },
  { path: 'organization_dashboards', label: '顧客分析' },
  { path: 'ai_chat_trips', label: 'AI手配予約' }
];

class Admin extends React.Component<Props, State> {
  static contextType = AITThemeContext;
  declare context: React.ContextType<typeof AITThemeContext>;

  constructor(props: Props) {
    super(props);
    this.state = {
      roleLoading: true,
      readablePaths: [],
      writablePaths: []
    };
  }

  componentDidMount() {
    this.fetchResources();
  }

  fetchResources() {
    Fetcher.get<AdminRoleResponse>('/admin/role.json').then(data => {
      const role = new AdminRole(data.role);
      const readablePaths = role.resources.map(resource => {
        if (!resource.readPermission) {
          return '';
        }
        return resource.resourcePath;
      });
      const writablePaths = role.resources.map(resource => {
        if (!resource.writePermission) {
          return '';
        }
        return resource.resourcePath;
      });
      this.setState({
        readablePaths,
        writablePaths,
        roleLoading: false
      });
    });
  }

  isReadable() {
    if (location.pathname === '/admin/edit') {
      return true;
    }

    const path =
      location.pathname.indexOf('admin/organizations') > 0
        ? '/admin/organizations'
        : location.pathname.match(/admin\/invoices\/\d+\/edit/)
        ? '/admin/invoices'
        : location.pathname.indexOf('admin/accounting') > 0
        ? '/admin/accounting/trial_balance'
        : location.pathname.indexOf('admin/online_applications') > 0
        ? '/admin/online_applications'
        : location.pathname.match(/arrange_categories\/\d+\/arrange_steps/)
        ? '/admin/arrange_categories'
        : location.pathname.match(/admin\/shareholder_ticket_fees\/\d+/)
        ? '/admin/shareholder_ticket_fees'
        : location.pathname.match(/arrange_categories\/\d+\/arrange_steps/) ||
          location.pathname.match(/admin\/suppliers\/\d+/) ||
          location.pathname.match(/admin\/supply_categories\/\d+/) ||
          location.pathname.indexOf('admin/master_step_sequences') > 0 ||
          location.pathname.indexOf('admin/master_step_mappings') > 0 ||
          location.pathname.indexOf('admin/master_step_settings') > 0 ||
          location.pathname.indexOf('admin/master_step_sequence_histories') > 0 ||
          location.pathname.indexOf('admin/supplier_histories') > 0 ||
          location.pathname.indexOf('admin/supplied_items') > 0
        ? '/admin/suppliers'
        : location.pathname.indexOf('admin/payment_methods') > 0 ||
          location.pathname.indexOf('admin/payment_method_histories') > 0
        ? '/admin/payment_methods'
        : location.pathname.indexOf('admin/supplied_item_mappings') > 0 ||
          location.pathname.indexOf('admin/individual_target_supplied_items') > 0 ||
          location.pathname.indexOf('admin/individual_railway_supplied_items') > 0 ||
          location.pathname.indexOf('admin/individual_domestic_air_supplied_items') > 0 ||
          location.pathname.indexOf('admin/individual_option_supplied_items') > 0
        ? '/admin/supplied_item_mappings'
        : location.pathname.indexOf('admin/organization_dashboards') > 0
        ? '/admin/organization_dashboards'
        : location.pathname.indexOf('admin/ai_chat_trips') > 0
        ? '/admin/ai_chat_trips'
        : location.pathname;

    return this.state.readablePaths.indexOf(path) >= 0;
  }

  isWritable() {
    return this.state.writablePaths.indexOf(location.pathname) >= 0;
  }

  render() {
    const {
      match: { path, url }
    } = this.props;
    const { theme } = this.context;
    const styles = getStyles(theme);

    try {
      return (
        <div style={styles.theAdmin}>
          <AdminHeader />
          <div style={styles.wrapper}>
            <div style={styles.content}>
              {this.state.roleLoading ? (
                <Loading />
              ) : this.isReadable() ? (
                <Suspense fallback={<></>}>
                  <Switch>
                    <Route
                      exact
                      path={path}
                      render={props => <Dashboard {...{ writable: this.isWritable() }} {...props} />}
                    />
                    <Route exact path={`${path}/organizations`} component={Organizations} />
                    <Route path={`${path}/organizations/:id`} component={OrganizationDetail} />
                    <Route exact path={`${path}/online_applications`} component={OnlineApplications} />
                    <Route exact path={`${path}/online_applications/new`} component={OnlineApplicationNew} />
                    <Route
                      exact
                      path={`${path}/online_applications/confirm`}
                      component={OnlineApplicationConfirm}
                    />
                    <Route exact path={`${path}/online_applications/:id/edit`} component={OnlineApplicationEdit} />
                    <Route
                      exact
                      path={`${path}/online_applications/:id/preview`}
                      component={OnlineApplicationPreview}
                    />
                    <Route exact path={`${path}/online_applications/:id`} component={OnlineApplicationDetail} />
                    <Route exact path={`${path}/invoices`} component={Invoices} />
                    <Route path={`${path}/invoices/:id/edit`} component={InvoicesEdit} />
                    <Route path={`${path}/accounting`} component={Accounting} />
                    <Route exact path={`${path}/edit`} component={AdminUserEdit} />
                    <Route exact path={`${path}/admins`} component={Admins} />
                    <Route exact path={`${path}/admin_roles`} component={AdminRoles} />
                    <Route exact path={`${path}/arrangers`} component={Arrangers} />
                    <Route exact path={`${path}/arrange_categories`} component={ArrangeCategories} />
                    <Route exact path={`${path}/master_step_sequences`} component={MasterStepSequences} />
                    <Route path={`${path}/master_step_sequences/:id`} component={MasterSteps} />
                    <Route exact path={`${path}/master_step_mappings`} component={MasterStepMappingsV2} />
                    <Route exact path={`${path}/master_step_mappings/v1`} component={MasterStepMappings} />
                    <Route exact path={`${path}/master_step_settings`} component={MasterStepSettings} />
                    <Route
                      exact
                      path={`${path}/master_step_sequence_histories`}
                      component={MasterStepSequenceHistories}
                    />
                    <Route
                      path={`${path}/master_step_sequence_histories/:id`}
                      component={MasterStepSequenceHistory}
                    />
                    <Route path={`${path}/arrange_categories/:id/arrange_steps`} component={ArrangeSteps} />
                    <Route exact path={`${path}/bulk_tickets`} component={BulkTicketsPage} />
                    <Route exact path={`${path}/manuals`} component={Manuals} />
                    <Route exact path={`${path}/knowledge_images`} component={Knowledge_images} />
                    <Route exact path={`${path}/informations`} component={Informations} />
                    <Route exact path={`${path}/arrangement_reports`} component={ArrangementReports} />
                    <Route exact path={`${path}/shareholder_ticket_fees`} component={ShareholderTicketFees} />
                    <Route path={`${path}/shareholder_ticket_fees/:id`} component={ShareholderTicketFees} />
                    <Route exact path={`${path}/holidays`} component={Holidays} />
                    <Route exact path={`${path}/suppliers`} component={Suppliers} />
                    <Route path={`${path}/suppliers/:id`} component={SupplierDetail} />
                    <Route path={`${path}/supply_categories/:id`} component={SupplyCategoryDetail} />
                    <Route exact path={`${path}/supplied_item_mappings`} component={SuppliedItemMappings} />
                    <Route
                      exact
                      path={`${path}/individual_target_supplied_items`}
                      component={IndividualTargetSuppliedItems}
                    />
                    <Route
                      exact
                      path={`${path}/individual_domestic_air_supplied_items`}
                      component={IndividualDomesticAirSuppliedItems}
                    />
                    <Route
                      exact
                      path={`${path}/individual_option_supplied_items`}
                      component={IndividualOptionSuppliedItems}
                    />
                    <Route
                      exact
                      path={`${path}/individual_railway_supplied_items`}
                      component={IndividualRailwaySuppliedItems}
                    />
                    <Route exact path={`${path}/supplier_histories`} component={SupplierHistories} />
                    <Route path={`${path}/supplier_histories/:id`} component={SupplierHistory} />
                    <Route path={`${path}/supplied_items`} component={SuppliedItems} />
                    <Route exact path={`${path}/supplier_cards`} component={SupplierCards} />
                    <Route
                      path={`${path}/repository_display_adjustment`}
                      component={RepositoryDisplayAdjustment}
                    />
                    <Route path={`${path}/audit_logs`} component={AuditLogs} />
                    <Route path={`${path}/payment_methods`} component={PaymentMethods} />
                    <Route path={`${path}/saving_accounts`} component={SavingAccounts} />
                    <Route exact path={`${path}/payment_method_histories`} component={PaymentMethodHistories} />
                    <Route path={`${path}/payment_method_histories/:id`} component={PaymentMethodHistory} />
                    <Route exact path={`${path}/organization_dashboards`} component={OrganizationDashboards} />
                    <Route path={`${path}/organization_dashboards/:id`} component={OrganizationDashboardDetail} />
                    <Route
                      exact
                      path={`${path}/organization_announcements`}
                      component={OrganizationAnnouncements}
                    />
                    <Route exact path={`${path}/ai_chat_trips`} component={AdminAiChatTrips} />
                    <Route path={`${path}/ai_chat_trips/:id`} component={AdminAiChatTripDetail} />
                    <Route component={NotFound} />
                  </Switch>
                </Suspense>
              ) : (
                <div style={styles.accessDenied}>
                  このページに対するアクセス権限がありません。管理者の方にご確認ください。
                </div>
              )}
            </div>
            <ul style={styles.naviUl}>
              {navItems.map(item => (
                <li key={item.path || 'dashboard'}>
                  <NaviItem to={item.path ? `${url}/${item.path}` : url}>{item.label}</NaviItem>
                </li>
              ))}
            </ul>
          </div>
          <Footer signedIn={false} />
        </div>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

export const Title = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h2 ref={ref} {...props} className={className ? `admin__title ${className}` : 'admin__title'} />
  )
);

const NaviItem: React.FC<LinkProps> = ({ className, ...props }) => {
  const { theme } = useAITTheme();
  const styles = getStyles(theme);

  return (
    <Link
      {...props}
      className={className ? `admin-navi__item ${className}` : 'admin-navi__item'}
      style={styles.naviItem}
    />
  );
};

const getStyles = (theme: AITTheme) => ({
  wrapper: {
    flexGrow: 9999,
    maxWidth: '100%',
    minWidth: 800,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    background: theme.tokens.colors.background.primary,
    padding: 20,
    display: 'flex' as const,
    flexDirection: 'row-reverse' as const,
    alignItems: 'stretch' as const,
    gap: 20
  },
  theAdmin: {
    fontSize: 13,
    minHeight: '100vh',
    display: 'flex' as const,
    flexDirection: 'column' as const
  },
  naviUl: {
    flex: '0 0 auto',
    width: 'max-content',
    display: 'flex' as const,
    flexDirection: 'column' as const,
    background: theme.tokens.colors.common.white,
    padding: 0,
    margin: 0
  },
  naviItem: {
    padding: '8px 10px',
    display: 'block' as const,
    fontWeight: 'bold' as const,
    whiteSpace: 'nowrap' as const,
    color: theme.tokens.colors.text.primary
  },
  content: {
    flex: '1 1 auto',
    width: 'auto',
    minWidth: 0,
    background: theme.tokens.colors.common.white,
    display: 'flex' as const,
    flexDirection: 'column' as const
  },
  accessDenied: {
    padding: '8px 10px'
  }
});

export default Admin;
